import React from 'react';
import { ArrowUpIcon } from 'lucide-react';
import { HeatmapChart } from './Charts';
import { books } from './constants';
import type { Book } from './types';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
interface EngagementTabProps {
  viewMode: string;
  selectedBook: Book;
  setSelectedBook: (book: Book) => void;
  setViewMode: (mode: string) => void;
}
export const EngagementTab = ({
  viewMode,
  selectedBook,
  setSelectedBook,
  setViewMode
}: EngagementTabProps) => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  // Referenced by table ternaries below but was never declared.
  const darkMode = dk.isDark;
  return <div className="space-y-6" data-id="element-5968" data-mp="i7xkl5omibgp">
      {/* Engagement Heatmap */}
      <div className={`rounded-lg shadow-sm p-6 border ${dk.card}`} data-id="element-5969" data-mp="15ybhb9i5kyhm">
        <div className="flex items-center justify-between mb-6" data-id="element-5970" data-mp="15x0dj9opjqqi">
          <div data-id="element-5971" data-mp="1aq1tzm4kbyc7">
            <h2 className={`text-lg font-medium ${dk.textPrimary}`} data-id="element-5972" data-mp="1aql1e8wl74bz">
              Reader Engagement Heatmap
            </h2>
            <p className={`text-sm ${dk.textMuted}`} data-id="element-5973" data-mp="3nqctczy89d22">
              {viewMode === 'all' ? 'See where readers are most engaged across your books' : `See where readers are most engaged in ${selectedBook.title}`}
            </p>
          </div>
        </div>
        <HeatmapChart data-id="element-5974" data-mp="3sq7jv2e3c0ml" />
      </div>

      {/* Reading Speed Analysis */}
      <div className={`rounded-lg shadow-sm p-6 border ${dk.card}`} data-id="element-5975" data-mp="achp3mvize37">
        <h2 className={`text-lg font-medium mb-2 ${dk.textPrimary}`} data-id="element-5976" data-mp="6c7jwcfk4rpe">
          Reading Speed Analysis
        </h2>
        <p className={`text-sm mb-6 ${dk.textMuted}`} data-id="element-5977" data-mp="kbyx4ay9242c">
          How quickly readers move through each chapter
        </p>
        <div className="space-y-4" data-id="element-5978" data-mp="fd5habvhq0z0">
          {[{
          label: 'Chapter 8-12',
          text: 'Slower reading pace - High engagement',
          width: '85%',
          color: 'bg-green-200'
        }, {
          label: 'Chapter 1-4',
          text: 'Average reading pace',
          width: '65%',
          color: 'bg-yellow-200'
        }, {
          label: 'Chapter 5-7',
          text: 'Faster reading pace - Lower engagement',
          width: '45%',
          color: 'bg-orange-200'
        }, {
          label: 'Chapter 13-15',
          text: 'Average reading pace',
          width: '70%',
          color: 'bg-yellow-200'
        }].map(({
          label,
          text,
          width,
          color
        }) => <div key={label} className="flex items-center" data-id="element-5979" data-mp="1dpi1ma4nobpb">
              <div className={`w-24 text-sm ${dk.textSecondary}`} data-id="element-5980" data-mp="1dq1jxry7x23v">{label}</div>
              <div className={`flex-1 h-8 rounded-md overflow-hidden relative ${dk.surfaceMuted}`} data-id="element-5981" data-mp="1hq1zjfx1zjci">
                <div className="absolute inset-0 flex items-center px-3 z-10" data-id="element-5982" data-mp="1ms106i1es25r">
                  <span className={`text-sm font-medium ${dk.textPrimary}`} data-id="element-5983" data-mp="1rq2iv1ux9zgp">
                    {text}
                  </span>
                </div>
                <div className={`${color} h-full`} style={{
              width
            }} data-id="element-5984" data-mp="1wuehu43l0jtr" />
              </div>
            </div>)}
        </div>
      </div>

      {/* Re-reads Analysis */}
      <div className={`rounded-lg shadow-sm p-6 border ${dk.card}`} data-id="element-5985" data-mp="1nv9n78v55amv">
        <h2 className={`text-lg font-medium mb-2 ${dk.textPrimary}`} data-id="element-5986" data-mp="2kio1azdbovhq">
          Re-reads & Returns
        </h2>
        <p className={`text-sm mb-6 ${dk.textMuted}`} data-id="element-5987" data-mp="2fl605v5jrkka">
          Sections readers revisit most frequently
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-5988" data-mp="2artb5jwyhbr0">
          <div data-id="element-5989" data-mp="26r9hwrfi6sa5">
            <h3 className={`text-sm font-medium mb-4 ${dk.textSecondary}`} data-id="element-5990" data-mp="26rsx40drr2kh">
              Most Re-read Chapters
            </h3>
            <div className="space-y-3" data-id="element-5991" data-mp="33c1oujephznb">
              {[{
              chapter: 9,
              percentage: 28
            }, {
              chapter: 10,
              percentage: 25
            }, {
              chapter: 12,
              percentage: 18
            }, {
              chapter: 8,
              percentage: 15
            }, {
              chapter: 11,
              percentage: 14
            }].map(item => <div key={item.chapter} className="flex items-center" data-id="element-5992" data-mp="31ub53sdv5icl">
                  <div className={`w-20 text-sm ${dk.textSecondary}`} data-id="element-5993" data-mp="36qjlf93srhlc">
                    Chapter {item.chapter}
                  </div>
                  <div className={`flex-1 h-6 rounded-full overflow-hidden ${dk.surfaceMuted}`} data-id="element-5994" data-mp="2x18ftwg4cv27">
                    <div className={`h-full rounded-full flex items-center px-2 ${rt.bgDark}`} style={{
                  width: `${item.percentage * 2}%`
                }} data-id="element-5995" data-mp="3kprowe6ztbvc">
                      <span className="text-xs text-white" data-id="element-5996" data-mp="193h75mqt6cdr">
                        {item.percentage}%
                      </span>
                    </div>
                  </div>
                </div>)}
            </div>
          </div>
          <div data-id="element-5997" data-mp="1iulkyj4qzr70">
            <h3 className={`text-sm font-medium mb-4 ${dk.textSecondary}`} data-id="element-5998" data-mp="1itskfzwdtxxb">
              Full Book Re-reads
            </h3>
            <div className="flex items-center space-x-4" data-id="element-5999" data-mp="v5j43dhxu2dk">
              <div className={`w-20 h-20 rounded-full border-8 flex items-center justify-center ${rt.borderAccent}`} data-id="element-6000" data-mp="q5wqv4240m4r">
                <span className={`text-xl font-bold ${dk.textPrimary}`} data-id="element-6001" data-mp="102st4s1f0c0v">
                  18%
                </span>
              </div>
              <div data-id="element-6002" data-mp="2gfzs5inzg6dw">
                <p className={`text-sm ${dk.textSecondary}`} data-id="element-6003" data-mp="2gfhm5ubiqdss">
                  of readers who finished the book read it again completely
                </p>
                <p className="text-xs text-green-600 flex items-center mt-1" data-id="element-6004" data-mp="2ilnyw99qptak">
                  <ArrowUpIcon className="h-3 w-3 mr-1" data-id="element-6005" data-mp="2nk8yq7l2yayh" />
                  4% from previous period
                </p>
              </div>
            </div>
            <h3 className={`text-sm font-medium mt-6 mb-2 ${dk.textSecondary}`} data-id="element-6006" data-mp="2sjvdilgk3z9e">
              Reader Sentiment
            </h3>
            <div className="space-y-1" data-id="element-6007" data-mp="2xg2lskwfdmui">
              <div className="flex items-center space-x-2" data-id="element-6008" data-mp="1zvg186ot7v1r">
                <div className="h-2 flex-1 bg-green-400 rounded-full" data-id="element-6009" data-mp="1zstto71x5fjj" />
                <span className={`text-xs ${dk.textSecondary}`} data-id="element-6010" data-mp="24u9hg6vhe05m">
                  85% Positive
                </span>
              </div>
              <div className="flex items-center space-x-2" data-id="element-6011" data-mp="2ejkntlqzgqpt">
                <div className="h-2 w-[12%] bg-gray-300 rounded-full" data-id="element-6012" data-mp="2ekvo3bl7zgc6" />
                <span className={`text-xs ${dk.textSecondary}`} data-id="element-6013" data-mp="1h340tphpv3u9">
                  12% Neutral
                </span>
              </div>
              <div className="flex items-center space-x-2" data-id="element-6014" data-mp="1m15hk3ro6r2k">
                <div className="h-2 w-[3%] bg-red-400 rounded-full" data-id="element-6015" data-mp="3740h9k1aa3s" />
                <span className={`text-xs ${dk.textSecondary}`} data-id="element-6016" data-mp="3vc7myaob6s5r">
                  3% Negative
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Book Comparison Table */}
      {viewMode === 'all' && <div className={`rounded-lg shadow-sm p-6 border ${dk.card}`} data-id="element-6017" data-mp="3ldiawyj3g1xf">
          <h2 className={`text-lg font-medium mb-4 ${dk.textPrimary}`} data-id="element-6018" data-mp="3gfgw4pspaorr">
            Book Engagement Comparison
          </h2>
          <div className="overflow-x-auto" data-id="element-6019" data-mp="3bmnnvry0wyax">
            <table className={`min-w-full divide-y ${dk.divider}`} data-id="element-6020" data-mp="37lu4nbvj07od">
              <thead className={`${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`} data-id="element-6021" data-mp="37jzf1k9b7rdy">
                <tr data-id="element-6022" data-mp="32p3m8hw90zzs">
                  {['Book', 'Start Rate', 'Completion Rate', 'Avg. Read Depth', 'Main Drop-off'].map(h => <th key={h} className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-6023" data-mp="ldmklpu3jn8e">
                      {h}
                    </th>)}
                </tr>
              </thead>
              <tbody className={`divide-y ${dk.divider}`} data-id="element-6024" data-mp="gdi1rt5pugf0">
                {books.map(book => <tr key={book.id} className={`cursor-pointer ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`} onClick={() => {
              setSelectedBook(book);
              setViewMode('individual');
            }} data-id="element-6025" data-mp="1p3rix5yzpof">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-6026" data-mp="3trlcafa1cmg3">
                      <div className="flex items-center" data-id="element-6027" data-mp="3exq9nr0h3tdc">
                        <img className="h-10 w-8 rounded object-cover mr-4" src={book.coverImage} alt="" data-id="element-6028" data-mp="3jv8m3rdp8qq3" />
                        <div data-id="element-6029" data-mp="2w8mu1ctxsujd">
                          <div className={`text-sm font-medium ${dk.textPrimary}`} data-id="element-6030" data-mp="2w9g34kgdbw4o">
                            {book.title}
                          </div>
                          <div className={`text-sm ${dk.textMuted}`} data-id="element-6031" data-mp="357i1es8pwao9">
                            {book.author}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${dk.textPrimary}`} data-id="element-6032" data-mp="3f567gebrx4lq">
                      {book.startRate}%
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${dk.textPrimary}`} data-id="element-6033" data-mp="3o1nbyypmu48i">
                      {book.completionRate}%
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${dk.textPrimary}`} data-id="element-6034" data-mp="1u0s3nxd6v6qd">
                      {book.averageReadDepth}%
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm ${dk.textPrimary}`} data-id="element-6035" data-mp="1kapbfzdw5wt3">
                      Ch. {book.mainDropoffChapter}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>}
    </div>;
};